import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Popconfirm,
  Space,
  Typography,
  Card,
  Tag,
  Upload,
  Rate,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { toastError, toastSuccess } from "../../../utils/toast";
import {
  fetchTestimonials,
  createTestimonial,
  updateTestimonial,
  deleteTestimonial,
} from "../../../api/testimonialsApi";
import { withBase } from "../../../config/env";

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function TestimonialsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchTestimonials();
      setRows(Array.isArray(data) ? data : []);
    } catch {
      toastError("Failed to fetch testimonials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const showCreateModal = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, display_order: 0, rating: 5 });
    setModalVisible(true);
  };

  const showEditModal = (record) => {
    setEditing(record);
    form.resetFields();
    form.setFieldsValue({
      client_name: record.client_name,
      role_title: record.role_title || "",
      company: record.company || "",
      review: record.review,
      rating: record.rating || 5,
      display_order: record.display_order || 0,
      is_active: Number(record.is_active) === 1,
      photo: record.photo
        ? {
            fileList: [
              {
                uid: "-1",
                name: "photo",
                status: "done",
                url: withBase(record.photo),
              },
            ],
          }
        : undefined,
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const fileObj = values.photo?.fileList?.[0]?.originFileObj;

      const payload = {
        client_name: values.client_name.trim(),
        role_title: (values.role_title || "").trim(),
        company: (values.company || "").trim(),
        review: values.review.trim(),
        rating: values.rating ?? 5,
        display_order: values.display_order ?? 0,
        is_active: values.is_active ? 1 : 0,
      };
      if (fileObj) payload.photo = fileObj;

      setSaving(true);
      if (editing) {
        await updateTestimonial(editing.id, payload);
        toastSuccess("Testimonial updated");
      } else {
        await createTestimonial(payload);
        toastSuccess("Testimonial created");
      }
      setModalVisible(false);
      load();
    } catch (err) {
      if (!err.errorFields) toastError("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteTestimonial(id);
      toastSuccess("Testimonial deleted");
      load();
    } catch {
      toastError("Delete failed");
    }
  };

  const columns = [
    {
      title: "Photo",
      dataIndex: "photo",
      width: 80,
      render: (val, record) =>
        val ? (
          <img
            src={withBase(val)}
            alt={record.client_name}
            style={{ width: 44, height: 44, objectFit: "cover", borderRadius: "50%" }}
          />
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Client",
      dataIndex: "client_name",
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          {(record.role_title || record.company) && (
            <div>
              <Text type="secondary">
                {[record.role_title, record.company].filter(Boolean).join(" · ")}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Review",
      dataIndex: "review",
      ellipsis: true,
    },
    {
      title: "Rating",
      dataIndex: "rating",
      width: 130,
      render: (value) => <Rate disabled value={Number(value) || 0} />,
    },
    {
      title: "Order",
      dataIndex: "display_order",
      width: 90,
      sorter: (a, b) => (a.display_order || 0) - (b.display_order || 0),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      width: 110,
      render: (isActive) => {
        const active = Number(isActive) === 1;
        return (
          <Tag icon={active ? <CheckOutlined /> : <CloseOutlined />} color={active ? "success" : "error"}>
            {active ? "Active" : "Inactive"}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            type="text"
            icon={<EditOutlined style={{ color: "#1890ff" }} />}
            onClick={() => showEditModal(record)}
          />
          <Popconfirm title="Delete this testimonial?" onConfirm={() => handleDelete(record.id)} okText="Delete">
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title level={4} style={{ margin: 0 }}>
              Testimonials
            </Title>
            <Button type="primary" icon={<PlusOutlined />} onClick={showCreateModal}>
              New Review
            </Button>
          </div>
        }
        bordered={false}
      >
        <Table
          columns={columns}
          dataSource={rows}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          bordered
        />
      </Card>

      <Modal
        title={editing ? "Edit Testimonial" : "New Testimonial"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSave}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="client_name" label="Client name" rules={[{ required: true, min: 2 }]}>
            <Input placeholder="Sarah Ahmed" />
          </Form.Item>
          <Form.Item name="role_title" label="Role / title">
            <Input placeholder="Managing Director" />
          </Form.Item>
          <Form.Item name="company" label="Company">
            <Input placeholder="Company name" />
          </Form.Item>
          <Form.Item name="review" label="Review" rules={[{ required: true, min: 10 }]}>
            <TextArea rows={5} placeholder="Write the client review shown on the homepage." />
          </Form.Item>
          <Form.Item name="rating" label="Rating">
            <Rate />
          </Form.Item>
          <Form.Item name="display_order" label="Display order">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="photo" label="Photo">
            <Upload beforeUpload={() => false} maxCount={1} listType="picture-card" accept="image/*">
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>Upload</div>
              </div>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
